export const buildNumberCandidates = (value: number, raw?: string) => {
  const candidates: string[] = [];

  const push = (candidate: string) => {
    if (!candidate) return;
    if (candidates.includes(candidate)) return;
    candidates.push(candidate);
  };

  const pushApprox = (candidate: string) => {
    if (!candidate) return;
    push(`~${candidate}`);
  };

  const abs = Math.abs(value);
  const isInteger = Number.isInteger(value);
  const isTiny = abs > 0 && abs < 1e-4;
  const rawText = raw?.trim();

  // Keep original scientific notation if the backend/CSV provided it (e.g. "1.55e-8").
  if (rawText && /e/i.test(rawText)) {
    push(rawText);
  } else if (isTiny) {
    // For very small values, default exponential is typically the most readable "exact" display.
    push(value.toExponential());
  }

  // Most informative (but potentially long) down to most compact.
  if (isInteger) {
    push(formatters.integer.format(value));
  } else {
    // "Unmodified" display: keep as much precision as possible before we start approximating.
    push(formatters.precise.format(value));

    // Approximate displays: reduced precision.
    for (const formatter of formatters.approxFractions) {
      pushApprox(formatter.format(value));
    }
  }

  if (abs >= 1_000) {
    for (const formatter of formatters.compactApprox) {
      pushApprox(formatter.format(value));
    }
  }

  // Approximate displays: fixed-precision exponential for scanning (not used when the raw string already contains `e`).
  if (isTiny && !(rawText && /e/i.test(rawText))) {
    for (const fractionDigits of [2, 3]) {
      pushApprox(value.toExponential(fractionDigits));
    }
  }

  // Approximate displays: scientific notation fallback.
  for (const formatter of formatters.scientificApprox) {
    pushApprox(formatter.format(value));
  }

  // Fallback (e.g. "-0" quirks).
  push(String(value));

  return candidates;
};

const formatters = {
  integer: new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }),
  precise: new Intl.NumberFormat(undefined, { maximumFractionDigits: 20 }),
  approxFractions: [
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 10 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 8 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }),
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }),
  ],
  compactApprox: [
    new Intl.NumberFormat(undefined, { notation: "compact", compactDisplay: "short", maximumFractionDigits: 2 }),
    new Intl.NumberFormat(undefined, { notation: "compact", compactDisplay: "short", maximumFractionDigits: 1 }),
    new Intl.NumberFormat(undefined, { notation: "compact", compactDisplay: "short", maximumFractionDigits: 0 }),
  ],
  scientificApprox: [
    new Intl.NumberFormat(undefined, { notation: "scientific", maximumSignificantDigits: 6 }),
    new Intl.NumberFormat(undefined, { notation: "scientific", maximumSignificantDigits: 4 }),
    new Intl.NumberFormat(undefined, { notation: "scientific", maximumSignificantDigits: 3 }),
    new Intl.NumberFormat(undefined, { notation: "scientific", maximumSignificantDigits: 2 }),
  ],
} satisfies Record<string, unknown>;
